/**
 * Enhanced Cache Manager with TTL (Time To Live)
 * Best practices for cache staleness based on data type
 */

// Cache duration constants (in milliseconds)
const CACHE_DURATIONS = {
  // Popular/Trending recipes - update weekly for freshness
  POPULAR: 7 * 24 * 60 * 60 * 1000, // 7 days

  // Search results - cache for a week (users often repeat searches)
  SEARCH: 7 * 24 * 60 * 60 * 1000, // 7 days

  // Cuisine pages - similar to search, weekly updates
  CUISINE: 7 * 24 * 60 * 60 * 1000, // 7 days

  // Recipe details - rarely change, cache longer
  RECIPE_DETAIL: 30 * 24 * 60 * 60 * 1000, // 30 days

  // API usage stats - persist indefinitely (until reset)
  API_STATS: Infinity,
};

class CacheManager {
  constructor() {
    this.cacheVersion = '2.1'; // Bumped: Image validation for search/cuisine results
    this.initIndexedDB();
  }

  /**
   * Initialize IndexedDB for larger storage capacity
   */
  async initIndexedDB() {
    if (!('indexedDB' in window)) {
      console.warn('IndexedDB not supported, falling back to localStorage');
      return;
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open('RecipeCache', 1);

      request.onerror = () => {
        console.warn('IndexedDB failed to open, using localStorage');
        resolve(false);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(true);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('recipes')) {
          db.createObjectStore('recipes', { keyPath: 'key' });
        }
      };
    });
  }

  /**
   * Get cache key with version
   */
  getCacheKey(key, type) {
    return `${this.cacheVersion}_${type}_${key}`;
  }

  /**
   * Store data in cache with TTL
   */
  async set(key, data, type = 'GENERIC') {
    const cacheKey = this.getCacheKey(key, type);
    const ttl = CACHE_DURATIONS[type] || CACHE_DURATIONS.SEARCH;
    const expiresAt = ttl === Infinity ? Infinity : Date.now() + ttl;

    const cacheItem = {
      key: cacheKey,
      data,
      expiresAt,
      cachedAt: Date.now(),
      type,
    };

    // Try IndexedDB first, fallback to localStorage
    if (this.db) {
      try {
        const transaction = this.db.transaction(['recipes'], 'readwrite');
        const store = transaction.objectStore('recipes');
        await store.put(cacheItem);
        return;
      } catch (error) {
        console.warn('IndexedDB write failed, using localStorage:', error);
      }
    }

    // Fallback to localStorage
    try {
      localStorage.setItem(cacheKey, JSON.stringify(cacheItem));
    } catch (error) {
      // Handle quota exceeded
      if (error.name === 'QuotaExceededError') {
        console.warn('localStorage quota exceeded, clearing old cache');
        this.clearExpired();
        try {
          localStorage.setItem(cacheKey, JSON.stringify(cacheItem));
        } catch (e) {
          console.error('Failed to cache after cleanup:', e);
        }
      }
    }
  }

  /**
   * Get data from cache (returns null if expired or not found)
   */
  async get(key, type = 'GENERIC') {
    const cacheKey = this.getCacheKey(key, type);

    // Try IndexedDB first
    if (this.db) {
      try {
        const transaction = this.db.transaction(['recipes'], 'readonly');
        const store = transaction.objectStore('recipes');
        const request = store.get(cacheKey);

        return new Promise((resolve) => {
          request.onsuccess = () => {
            const item = request.result;
            if (!item) {
              resolve(null);
              return;
            }

            // Check if expired
            if (item.expiresAt !== Infinity && item.expiresAt < Date.now()) {
              // Expired - delete and return null
              this.delete(key, type);
              resolve(null);
              return;
            }

            resolve(item.data);
          };

          request.onerror = () => {
            // Fallback to localStorage
            resolve(this.getFromLocalStorage(cacheKey));
          };
        });
      } catch (error) {
        console.warn('IndexedDB read failed, using localStorage:', error);
      }
    }

    // Fallback to localStorage
    return this.getFromLocalStorage(cacheKey);
  }

  /**
   * Get from localStorage (with expiration check)
   */
  getFromLocalStorage(cacheKey) {
    try {
      const item = localStorage.getItem(cacheKey);
      if (!item) return null;

      const cacheItem = JSON.parse(item);

      // Check if expired
      if (cacheItem.expiresAt !== Infinity && cacheItem.expiresAt < Date.now()) {
        // Expired - delete and return null
        localStorage.removeItem(cacheKey);
        return null;
      }

      return cacheItem.data;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return null;
    }
  }

  /**
   * Delete specific cache item
   */
  async delete(key, type = 'GENERIC') {
    const cacheKey = this.getCacheKey(key, type);

    // Delete from IndexedDB
    if (this.db) {
      try {
        const transaction = this.db.transaction(['recipes'], 'readwrite');
        const store = transaction.objectStore('recipes');
        await store.delete(cacheKey);
      } catch (error) {
        console.warn('IndexedDB delete failed:', error);
      }
    }

    // Delete from localStorage
    try {
      localStorage.removeItem(cacheKey);
    } catch (error) {
      console.error('Error deleting from localStorage:', error);
    }
  }

  /**
   * Clear all expired cache items
   */
  async clearExpired() {
    const now = Date.now();
    const keysToDelete = [];

    // Clear from localStorage
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key) continue;

        try {
          const item = JSON.parse(localStorage.getItem(key));
          if (item.expiresAt !== Infinity && item.expiresAt < now) {
            keysToDelete.push(key);
          }
        } catch (e) {
          // Invalid JSON, skip
        }
      }

      keysToDelete.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.error('Error clearing expired localStorage:', error);
    }

    // Clear from IndexedDB
    if (this.db) {
      try {
        const transaction = this.db.transaction(['recipes'], 'readwrite');
        const store = transaction.objectStore('recipes');
        const request = store.openCursor();

        request.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            const item = cursor.value;
            if (item.expiresAt !== Infinity && item.expiresAt < now) {
              cursor.delete();
            }
            cursor.continue();
          }
        };
      } catch (error) {
        console.error('Error clearing expired IndexedDB:', error);
      }
    }
  }

  /**
   * Clear all cache (use with caution)
   */
  async clearAll() {
    // Clear localStorage (keep API stats)
    try {
      const apiStats = localStorage.getItem('api_usage_stats');
      localStorage.clear();
      if (apiStats) {
        localStorage.setItem('api_usage_stats', apiStats);
      }
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }

    // Clear IndexedDB
    if (this.db) {
      try {
        const transaction = this.db.transaction(['recipes'], 'readwrite');
        const store = transaction.objectStore('recipes');
        await store.clear();
      } catch (error) {
        console.error('Error clearing IndexedDB:', error);
      }
    }
  }

  /**
   * Get cache statistics
   */
  async getStats() {
    let localStorageCount = 0;
    let indexedDBCount = 0;
    let totalSize = 0;

    // Count localStorage items
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.cacheVersion)) {
          localStorageCount++;
          const item = localStorage.getItem(key);
          if (item) {
            totalSize += new Blob([item]).size;
          }
        }
      }
    } catch (error) {
      console.error('Error counting localStorage:', error);
    }

    // Count IndexedDB items
    if (this.db) {
      try {
        const transaction = this.db.transaction(['recipes'], 'readonly');
        const store = transaction.objectStore('recipes');
        const request = store.count();

        indexedDBCount = await new Promise((resolve) => {
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => resolve(0);
        });
      } catch (error) {
        console.error('Error counting IndexedDB:', error);
      }
    }

    return {
      localStorageItems: localStorageCount,
      indexedDBItems: indexedDBCount,
      totalItems: localStorageCount + indexedDBCount,
      estimatedSize: totalSize,
      sizeInMB: (totalSize / 1024 / 1024).toFixed(2),
    };
  }
}

// Singleton instance
const cacheManager = new CacheManager();

// Clean up expired items on load (runs once)
cacheManager.clearExpired();

export default cacheManager;
